import { mcpConnectionTest } from "../mcpConnectionTest";
import { oauthSetupSteps } from "../oauthSetupSteps";
import {
  dropUnsetTokenResponseFields,
  oauthAuth,
} from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "../atlassianAdmin/logo.svg";

const MCP_SERVER_URL = "https://mcp.atlassian.com/v2/mcp";

export const atlassian = defineProduct({
  name: "Atlassian",
  tagLine: "Jira, Confluence, and more",
  logo,
  tags: ["productivity"],
  variants: [
    productVariant({
      identifier: "atlassian",
      authLabel: "OAuth app",
      protocol: "http",
      defaultUrlPattern: () =>
        "https://api\\.atlassian\\.com/(?:oauth/token/accessible-resources(?:\\?.*)?|ex/(?:jira|confluence)/.*)$",
      auth: oauthAuth,
      steps: oauthSetupSteps({
        identifier: "atlassian",
        registrationUrl: "https://developer.atlassian.com/console/myapps/",
        scopes: ["read:jira-user", "offline_access"],
        instructions:
          "Configure a distributable OAuth 2.0 (3LO) application and enable the Jira or Confluence API scopes required by your workflow. Request offline_access for renewable tokens. The authorizing user’s permissions also limit API access.",
      }),
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () => "https://auth.atlassian.com/authorize",
        tokenEndpoint: () => "https://auth.atlassian.com/oauth/token",
        tokenEndpointAuthMethod: () => "client_secret_post",
        tokenRequestFormat: () => "json",
        clientCredentialsFrom: "db",
        scopes: () => ["read:jira-user", "offline_access"],
        authorizationParameters: () => ({
          audience: "api.atlassian.com",
          prompt: "consent",
        }),
        pkce: () => false,
      },
      connectionTest: {
        protocol: "http",
        request: () =>
          "https://api.atlassian.com/oauth/token/accessible-resources",
      },
      usageHint: () =>
        "First GET https://api.atlassian.com/oauth/token/accessible-resources to find the authorized site’s cloud ID and scopes. Use https://api.atlassian.com/ex/jira/{cloudId}/rest/ or /ex/confluence/{cloudId}/wiki/rest/ for product API calls. A valid token can have no authorized sites; confirm that the required site appears in the resource list.",
    }),
    productVariant({
      identifier: "atlassianMcp",
      authLabel: "MCP OAuth",
      protocol: "http",
      defaultUrlPattern: () =>
        "https://mcp\\.atlassian\\.com/v2/mcp(?:\\?.*)?$",
      auth: () => oauthAuth,
      // Atlassian replaces the authorization server behind this URL, so sign-in reads the endpoints from discovery.
      oauth: {
        grantType: () => "authorization_code",
        clientCredentialsFrom: "dcr",
        discoverFrom: () => MCP_SERVER_URL,
        authorizationEndpoint: (cfg) =>
          (cfg as Record<string, string>).authorizeUrl ?? "",
        tokenEndpoint: (cfg) => (cfg as Record<string, string>).tokenUrl ?? "",
        issuer: (cfg) => (cfg as Record<string, string>).issuer ?? "",
        tokenEndpointAuthMethod: (_cfg, { hasSecret }) =>
          hasSecret ? "client_secret_post" : "none",
        pkce: () => true,
        resource: (cfg) =>
          (cfg as Record<string, string>).resource ?? MCP_SERVER_URL,
        scopes: () => [
          "read:me",
          "read:account",
          "offline_access",
          "read:jira:agent-interface",
          "read:confluence:agent-interface",
        ],
        normalizeTokenResponse: dropUnsetTokenResponseFields,
      },
      steps: [
        {
          title: "Sign in with Atlassian",
          items: [
            {
              kind: "markdown",
              content:
                "Sign in with the Atlassian account this connector should use. This connects to Atlassian’s MCP server. For Jira and Confluence REST requests, choose **OAuth app**.",
            },
            { kind: "oauth" },
          ],
        },
      ],
      connectionTest: mcpConnectionTest(MCP_SERVER_URL),
      usageHint: () =>
        `Call Atlassian through streamable HTTP MCP at ${MCP_SERVER_URL}. Load the mcp-client skill for the handshake and tool calls; authentication is injected automatically.`,
    }),
  ],
});
